import learningStreakService, {
  FreezePeriod,
  StreakStatus
} from '@/services/learning-streak.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Clock,
  Flame,
  Loader2,
  Shield,
  Snowflake, Target, TrendingUp
} from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';

interface StreakWidgetProps {
  compact?: boolean;
  showActions?: boolean;
}

const StreakStatusBadge = ({ status }: { status: StreakStatus }) => {
  const getStatusConfig = (status: StreakStatus) => {
    switch (status) {
      case 'ACTIVE':
        return { icon: <Flame className="w-4 h-4" />, color: 'bg-green-100 text-green-700', label: 'Active' };
      case 'BROKEN':
        return { icon: <AlertTriangle className="w-4 h-4" />, color: 'bg-red-100 text-red-700', label: 'Broken' };
      case 'FROZEN':
        return { icon: <Snowflake className="w-4 h-4" />, color: 'bg-blue-100 text-blue-700', label: 'Frozen' };
      case 'AT_RISK':
        return { icon: <AlertTriangle className="w-4 h-4" />, color: 'bg-yellow-100 text-yellow-700', label: 'At Risk' };
      default:
        return { icon: <Clock className="w-4 h-4" />, color: 'bg-gray-100 text-gray-700', label: 'Unknown' };
    }
  };

  const config = getStatusConfig(status);
  
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.icon}
      {config.label}
    </div>
  );
};

export default function StreakWidget({ compact = false, showActions = true }: StreakWidgetProps) {
  const queryClient = useQueryClient();
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [selectedDays, setSelectedDays] = useState<FreezePeriod>(3);

  // Fetch current streak
  const { data: streak, isLoading, error } = useQuery({
    queryKey: ['learning-streak-current'],
    queryFn: () => learningStreakService.getCurrentStreak(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000 // Refetch every 10 minutes
  });

  // Freeze streak mutation
  const freezeMutation = useMutation({
    mutationFn: (days: FreezePeriod) => learningStreakService.freezeStreak(days),
    onSuccess: (data) => {
      toast.success(`Streak frozen for ${selectedDays} days!`);
      queryClient.invalidateQueries({ queryKey: ['learning-streak-current'] });
      setShowFreezeModal(false);
    },
    onError: (error: any) => {
      const errorMessage = error.message || 'Failed to freeze streak';
      toast.error(errorMessage);
    }
  });

  const handleFreeze = () => {
    if (streak && learningStreakService.canFreezeStreak(streak).canFreeze) {
      freezeMutation.mutate(selectedDays);
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 ${compact ? 'p-3' : 'p-4'}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-8 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error || !streak) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 ${compact ? 'p-3' : 'p-4'}`}>
        <div className="flex items-center gap-2 text-red-600">
          <AlertTriangle className="w-5 h-5" />
          <span className="text-sm">Failed to load streak data</span>
        </div>
      </div>
    );
  }

  const milestoneProgress = learningStreakService.getMilestoneProgress(streak.current_streak);
  const canFreeze = learningStreakService.canFreezeStreak(streak);

  if (compact) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-xs">Learning Streak</p>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold flex items-center gap-1 text-white">
                {streak.current_streak}
                <Flame className="w-5 h-5 text-orange-300" />
              </p>
              {streak.streak_frozen && (
                <Snowflake className="w-4 h-4 text-blue-300" />
              )}
            </div>
          </div>
        </div>
        {streak.at_risk && (
          <div className="mt-1 text-xs text-yellow-200">
            ⚠️ Complete learning today to maintain streak
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-600" />
            Learning Streak
          </h3>
          <StreakStatusBadge status={streak.streak_status} />
        </div>

        {/* Current Streak */}
        <div className="text-center mb-4">
          <div className="text-4xl font-bold text-gray-900 flex items-center justify-center gap-2">
            {streak.current_streak}
            <Flame className="w-8 h-8 text-orange-500" />
          </div>
          <p className="text-gray-600">Current Streak Days</p>
          {streak.streak_frozen && (
            <p className="text-sm text-blue-600 mt-1 flex items-center justify-center gap-1">
              <Snowflake className="w-4 h-4" />
              Frozen for {streak.freeze_days_remaining} more days
            </p>
          )}
        </div>

        {/* Progress to Next Milestone */}
        {milestoneProgress.next && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Next milestone</span>
              <span>{milestoneProgress.current}/{milestoneProgress.next} days</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${milestoneProgress.percentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center mb-1">
              <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
              <span className="text-lg font-semibold text-gray-900">
                {streak.longest_streak}
              </span>
            </div>
            <p className="text-xs text-gray-600">Longest Streak</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center mb-1">
              <Target className="w-4 h-4 text-blue-600 mr-1" />
              <span className="text-lg font-semibold text-gray-900">
                {streak.total_learning_days}
              </span>
            </div>
            <p className="text-xs text-gray-600">Total Days</p>
          </div>
        </div>

        {/* At Risk Warning */}
        {streak.at_risk && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 text-yellow-800">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">Streak at risk!</span>
            </div>
            <p className="text-xs text-yellow-700 mt-1">
              Complete at least 10 minutes of learning in the next{' '}
              {learningStreakService.formatTimeUntilReset(streak.hours_until_reset)} to maintain your streak.
            </p>
          </div>
        )}

        {/* Actions */}
        {/* {showActions && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowFreezeModal(true)}
              disabled={!canFreeze.canFreeze || freezeMutation.isLoading}
              className="flex-1 px-3 py-2 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium flex items-center justify-center gap-2"
              title={!canFreeze.canFreeze ? canFreeze.reason : 'Freeze your streak'}
            >
              <Shield className="w-4 h-4" />
              Freeze Streak
            </button>
            <Link
              to="/learn/streak-leaderboard"
              className="px-3 py-2 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700 text-sm font-medium flex items-center justify-center gap-2"
            >
              <TrendingUp className="w-4 h-4" />
              Leaderboard
            </Link>
          </div>
        )} */}
      </div>

      {/* Freeze Modal */}
      {showFreezeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Freeze Your Streak</h3>
            <p className="text-gray-600 text-sm mb-4">
              Protect your {streak.current_streak}-day streak by freezing it. You can continue learning without worry of losing your progress.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Freeze Duration
              </label>
              <div className="space-y-2">
                {learningStreakService.getFreezePeriodOptions().map((option) => (
                  <label key={option.value} className="flex items-center">
                    <input
                      type="radio"
                      name="freezeDays"
                      value={option.value}
                      checked={selectedDays === option.value}
                      onChange={(e) => setSelectedDays(Number(e.target.value) as FreezePeriod)}
                      className="mr-2"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowFreezeModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleFreeze}
                disabled={freezeMutation.isLoading}
                className="flex-1 px-4 py-2 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {freezeMutation.isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Shield className="w-4 h-4" />
                )}
                Freeze
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}